/**
 * Reject Dialog (Spoor 2, Verlof MVP — Story 3.2, UX V5)
 *
 * Afwijs-dialoog met verplicht redenveld. AC 2: de knop `Afwijzen` blijft
 * disabled tot de reden is ingevuld — een UI-laag-verdediging bovenop de
 * verplichte-bij-afwijzen zod-refine op de server (`decideLeaveRequestSchema`,
 * AD-4: de server-check is bindend, de UI verbergt/disabled alleen als extra
 * laag).
 *
 * Story 2.6 (AC-7/AC-12) voegt twee dingen toe, allebei optioneel zodat de
 * bestaande aanvraag-afwijzing zich exact gedraagt als voorheen:
 * - `blijftOpUren`: de contextafhankelijke regel *De uren blijven op 2 uur
 *   staan.* (letterlijk uit de microcopy-tabel, UX-DR14).
 * - een zichtbare, aan het veld gekoppelde foutmelding onder het redenveld —
 *   een uitgeschakelde knop alleen vertelt een schermlezer niet wat er mist.
 *   De tekst is dezelfde als de server hanteert (`decideLeaveRequestSchema` /
 *   `decideLeaveCorrectionSchema`), dus geen nieuw bedachte copy.
 *
 * Custom modal, zelfde overlay-conventie als `LeaveRequestSheet.tsx`
 * (dit project heeft geen shadcn/ui-dialog-component).
 */
import { useEffect, useState } from 'react';
import { formatNlUren } from './urenFormat';

const REDEN_FOUT_ID = 'reject-reason-fout';
const CONTEXT_ID = 'reject-reason-context';
const TOELICHTING_ID = 'reject-reason-toelichting';

interface RejectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isPending?: boolean;
  /**
   * Story 2.6 (AC-7): de uren die blijven staan wanneer deze urenwijziging
   * wordt afgewezen. Weglaten bij een gewone verlofaanvraag — dan verschijnt
   * er geen contextregel.
   */
  blijftOpUren?: number;
  /**
   * Story 7.3 (AC-7/AC-13): de regel die bij het redenveld hoort te staan —
   * voor HR de §10.6-tekst over wie de reden te zien krijgt. Hij staat er
   * vanaf het openen (niet pas na een fout) en hangt via `aria-describedby`
   * aan het veld, zodat een schermlezer hem hoort vóórdat er getypt wordt.
   * Weglaten bij elk ander besluit — dan verandert er niets aan deze dialoog.
   */
  redenToelichting?: string;
}

export function RejectDialog({
  isOpen,
  onClose,
  onConfirm,
  isPending = false,
  blijftOpUren,
  redenToelichting,
}: RejectDialogProps) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setTouched(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [isOpen, onClose, isPending]);

  if (!isOpen) return null;

  const canConfirm = reason.trim().length > 0 && !isPending;
  const toonRedenFout = touched && reason.trim().length === 0;

  const describedBy = [
    blijftOpUren !== undefined ? CONTEXT_ID : null,
    redenToelichting !== undefined ? TOELICHTING_ID : null,
    toonRedenFout ? REDEN_FOUT_ID : null,
  ]
    .filter((id): id is string => id !== null)
    .join(' ');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      data-testid="reject-dialog-overlay"
    >
      <div className="w-full max-w-md bg-white rounded-lg p-6 shadow-xl" data-testid="reject-dialog">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#1E293B]">
            {blijftOpUren !== undefined ? 'Urenwijziging afwijzen' : 'Aanvraag afwijzen'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {blijftOpUren !== undefined && (
          <p id={CONTEXT_ID} data-testid="reject-context" className="mb-4 text-sm text-gray-700">
            {`De uren blijven op ${formatNlUren(blijftOpUren)} uur staan.`}
          </p>
        )}

        {redenToelichting !== undefined && (
          <p
            id={TOELICHTING_ID}
            data-testid="reject-reden-toelichting"
            className="mb-2 text-sm text-gray-700"
          >
            {redenToelichting}
          </p>
        )}

        <label htmlFor="reject-reason" className="block text-sm text-gray-600 mb-1">
          Reden (verplicht — zichtbaar voor de medewerker)
        </label>
        <textarea
          id="reject-reason"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setTouched(true);
          }}
          onBlur={() => setTouched(true)}
          rows={4}
          aria-describedby={describedBy || undefined}
          aria-invalid={toonRedenFout || undefined}
          className="w-full border border-[#E2E8F0] rounded-md p-2 text-sm"
          placeholder="Bijv. te druk in die periode"
        />

        {toonRedenFout && (
          <p
            id={REDEN_FOUT_ID}
            data-testid="reject-reden-fout"
            role="alert"
            className="mt-1 text-xs text-[#D64545]"
          >
            Reden is verplicht bij afwijzen
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={!canConfirm}
            className="px-4 py-2 rounded-md bg-[#D64545] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Afwijzen
          </button>
        </div>
      </div>
    </div>
  );
}
